import { useBookingTrips } from '@/hooks/useBookingTrips';
import type { BookedTripDTO, BookingDTO } from '@/types/dto/agent';
import {
  getAncillariesByLegId,
  isRefundableAncillary,
} from '@/utils/ancillary';
import { isTimestampInPast } from '@/utils/dateTime';
import { useCallback, useMemo } from 'react';

const BOOKING_TIMEZONE = 'Europe/Stockholm';

export const isLegDepartureUpcoming = (leg: { departureTime?: string }) =>
  Boolean(
    leg.departureTime && !isTimestampInPast(leg.departureTime, BOOKING_TIMEZONE)
  );

const hasNonPastLeg = (trip: BookedTripDTO) =>
  trip.legs?.some(isLegDepartureUpcoming) ?? false;

export const getExtrasSelectionTrips = (trips: Array<BookedTripDTO>) =>
  trips.filter(hasNonPastLeg).map((trip) => ({
    ...trip,
    legs: trip.legs?.filter(isLegDepartureUpcoming),
  }));

export const useBookingRefundEligibleExtras = (booking: BookingDTO | null) => {
  const { outboundTrips: allOutboundTrips, inboundTrips: allInboundTrips } =
    useBookingTrips(booking);
  const ancillariesByLegId = useMemo(
    () => getAncillariesByLegId(booking),
    [booking]
  );

  const hasRefundableExtrasOnLeg = useCallback(
    (legId: string) =>
      (ancillariesByLegId[legId] ?? []).some(isRefundableAncillary),
    [ancillariesByLegId]
  );

  const extrasEligibleTrips = useCallback(
    (trips: Array<BookedTripDTO>) =>
      trips.filter(
        (trip) =>
          hasNonPastLeg(trip) &&
          trip.legs?.some(
            (leg) =>
              leg.id &&
              isLegDepartureUpcoming(leg) &&
              hasRefundableExtrasOnLeg(leg.id)
          )
      ),
    [hasRefundableExtrasOnLeg]
  );

  const extrasSelectionTrips = useCallback(
    (trips: Array<BookedTripDTO>) => getExtrasSelectionTrips(trips),
    []
  );

  const extrasEligibleOutboundTrips = useMemo(
    () => extrasEligibleTrips(allOutboundTrips),
    [allOutboundTrips, extrasEligibleTrips]
  );
  const extrasEligibleInboundTrips = useMemo(
    () => extrasEligibleTrips(allInboundTrips),
    [allInboundTrips, extrasEligibleTrips]
  );
  const extrasSelectionOutboundTrips = useMemo(
    () => extrasSelectionTrips(allOutboundTrips),
    [allOutboundTrips, extrasSelectionTrips]
  );
  const extrasSelectionInboundTrips = useMemo(
    () => extrasSelectionTrips(allInboundTrips),
    [allInboundTrips, extrasSelectionTrips]
  );

  const areEligibleExtrasRefundable = useMemo(
    () =>
      Boolean(
        booking?.bookedTrips?.length &&
          (extrasEligibleOutboundTrips.length ||
            extrasEligibleInboundTrips.length)
      ),
    [
      booking?.bookedTrips?.length,
      extrasEligibleInboundTrips.length,
      extrasEligibleOutboundTrips.length,
    ]
  );

  return {
    ancillariesByLegId,
    areEligibleExtrasRefundable,
    extrasSelectionInboundTrips,
    extrasSelectionOutboundTrips,
  };
};
